import React, { useEffect, useState } from "react";
import { X, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Founding Member lead capture modal.
 * Fires when "Lock in" / "Reserve Your VIP Spot" buttons are clicked.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - tier?: "activate" | "accelerate" | "interested"  (preselected)
 *  - source?: string  (analytics tag — e.g. "landing-hero", "tier-card", "blog-cta")
 */
export default function FoundingLeadModal({ open, onClose, tier = "interested", source = "landing" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      // Reset on re-open
      setDone(false);
      setError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/founding", {
        ...form,
        tier,
        source,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fld = "w-full rounded-lg px-4 py-3 text-base font-medium outline-none transition-colors bg-white border border-[var(--lh-border)] text-[var(--lh-espresso-noir)] focus:border-[var(--lh-peach)] focus:ring-2 focus:ring-[var(--lh-peach)]/30";

  const tierCopy = {
    activate: "Activate · $29/mo founding rate",
    accelerate: "Accelerate · $89/mo founding rate",
    interested: "Be first when memberships go live",
  }[tier];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="founding-lead-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="founding-lead-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[var(--lh-espresso-noir)]/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: "var(--lh-warm-linen)", border: "1px solid var(--lh-border)" }}
      >
        {/* Peach header bar */}
        <div
          className="px-7 py-4 flex items-center justify-between"
          style={{ background: "var(--lh-peach)" }}
        >
          <div className="overline" style={{ color: "var(--lh-espresso-noir)" }}>
            Founding Member · VIP List
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-full hover:bg-[var(--lh-espresso-noir)]/10 transition-colors"
            data-testid="founding-lead-close"
          >
            <X size={18} style={{ color: "var(--lh-espresso-noir)" }} />
          </button>
        </div>

        <div className="px-7 py-8 lg:py-10">
          {done ? (
            <div className="text-center py-6" data-testid="founding-lead-success">
              <CheckCircle2
                size={48}
                className="mx-auto mb-5"
                style={{ color: "var(--lh-cognac)" }}
                aria-hidden="true"
              />
              <h2 className="font-display-bold text-4xl tracking-tighter mb-3 leading-[1.0]">
                You're <em>on the list.</em>
              </h2>
              <p className="text-base font-medium text-[var(--lh-espresso-noir)] max-w-sm mx-auto">
                Your VIP spot is locked in. Memberships launch this <strong>Summer</strong>.
                We'll be in touch the moment doors open.
              </p>
              <button
                onClick={onClose}
                className="btn-primary mt-8"
                data-testid="founding-lead-done"
              >
                Got it <ArrowUpRight size={14} />
              </button>
            </div>
          ) : (
            <>
              <h2
                id="founding-lead-title"
                className="font-display-bold text-3xl md:text-4xl tracking-tighter leading-[1.0] mb-2"
              >
                Reserve your <em>VIP spot.</em>
              </h2>
              <p className="text-sm font-medium text-[var(--lh-espresso-noir)] mb-1">
                {tierCopy}
              </p>
              <p className="text-sm text-[var(--lh-muted)] mb-7">
                Memberships launch this <strong>Summer</strong>. Drop your details — we'll
                reserve your spot and let you know the moment doors open.
              </p>

              <form onSubmit={submit} className="space-y-3" data-testid="founding-lead-form">
                <input
                  type="text"
                  required
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={fld}
                  data-testid="founding-lead-name"
                  autoFocus
                />
                <input
                  type="email"
                  required
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={fld}
                  data-testid="founding-lead-email"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Business name"
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className={fld}
                    data-testid="founding-lead-business"
                  />
                  <input
                    type="text"
                    placeholder="City"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className={fld}
                    data-testid="founding-lead-city"
                  />
                </div>
                <textarea
                  placeholder="Anything you'd like us to know? (optional)"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={fld}
                  data-testid="founding-lead-message"
                />

                {error && (
                  <div
                    className="text-sm font-semibold"
                    style={{ color: "#B43A23" }}
                    data-testid="founding-lead-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center mt-2"
                  data-testid="founding-lead-submit"
                >
                  {submitting ? "Reserving…" : "Reserve My VIP Spot"} <ArrowUpRight size={15} />
                </button>

                <p className="text-[0.7rem] text-center mt-3" style={{ color: "var(--lh-muted)" }}>
                  No payment required. We'll only email you about LocalizeHER.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
